'use client';

import React, { useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { LENIS_READY_EVENT } from '../SmoothScrollWrapper/SmoothScrollWrapper';
import styles from './CmsParagraphReveal.module.css';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const normalizeBlockIds = blockIds => {
  if (!blockIds) return [];
  return Array.isArray(blockIds) ? blockIds : [blockIds];
};

const DEFAULT_TARGET_SELECTORS = ['p', 'ul li'];

const WORD_ATTRIBUTE = 'data-reveal-word';
const SPLIT_ATTRIBUTE = 'data-reveal-split';

let refreshRaf = null;
const scheduleScrollTriggerRefresh = () => {
  if (typeof window === 'undefined') return;
  if (refreshRaf != null) cancelAnimationFrame(refreshRaf);
  refreshRaf = requestAnimationFrame(() => {
    refreshRaf = requestAnimationFrame(() => {
      refreshRaf = null;
      ScrollTrigger.refresh();
    });
  });
};

/**
 * Wrap every word of every text node below `root` in its own span.
 * Whitespace stays a regular space so the browser keeps line-break opportunities.
 */
const splitIntoWords = root => {
  if (root.hasAttribute(SPLIT_ATTRIBUTE)) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach(textNode => {
    const text = textNode.nodeValue;
    if (!text || !text.trim() || !textNode.parentNode) return;

    const fragment = document.createDocumentFragment();
    text.split(/(\s+)/).forEach(part => {
      if (!part) return;
      if (/^\s+$/.test(part)) {
        fragment.appendChild(document.createTextNode(' '));
        return;
      }
      const word = document.createElement('span');
      word.className = styles.word;
      word.setAttribute(WORD_ATTRIBUTE, '');
      word.textContent = part;
      fragment.appendChild(word);
    });

    textNode.parentNode.replaceChild(fragment, textNode);
  });

  root.setAttribute(SPLIT_ATTRIBUTE, '');
};

/**
 * Prefer innermost matches so `ul li > p` is split once on the paragraph,
 * not again on the list item.
 */
const collectSplitRoots = (container, selectors) => {
  const targets = Array.from(container.querySelectorAll(selectors.join(', ')));
  return targets.filter(
    el => !targets.some(other => other !== el && el.contains(other))
  );
};

/**
 * Scroll-triggered word-by-word reveal for PageBuilder (CMS) block text.
 * Renders Field children as-is so markdown structure (p, h4, ul/li) is kept,
 * then splits words only in the configured selectors (default: p, ul li).
 */
export default function CmsParagraphReveal({
  children,
  className = '',
  animate = true,
  blockId,
  blockIds = [],
  targetSelectors = DEFAULT_TARGET_SELECTORS,
  marker = false,
  start = 'top 85%',
  end = 'top 60%',
  pinned = true,
  staggerEach = 0.04,
  wordDuration = 0.3,
  once = false,
  delay = 0,
}) {
  const containerRef = useRef(null);
  const allowedIds = normalizeBlockIds(blockIds);
  const shouldAnimate = animate && blockId && allowedIds.includes(blockId);
  const selectorKey = Array.isArray(targetSelectors)
    ? targetSelectors.join(',')
    : String(targetSelectors || '');

  useLayoutEffect(() => {
    if (!shouldAnimate) return;

    const container = containerRef.current;
    if (!container) return;

    const selectors = selectorKey
      .split(',')
      .map(selector => selector.trim())
      .filter(Boolean);
    if (selectors.length === 0) return;

    const splitRoots = collectSplitRoots(container, selectors);
    if (splitRoots.length === 0) return;

    const ctx = gsap.context(() => {
      splitRoots.forEach(target => {
        splitIntoWords(target);
        const words = target.querySelectorAll(`[${WORD_ATTRIBUTE}]`);
        if (words.length === 0) return;

        gsap.set(words, { opacity: 0.1 });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: target,
            start,
            end,
            scrub: true,
            once,
            markers: marker,
            invalidateOnRefresh: true,
            toggleActions: pinned ? 'play none none none' : 'play none none reverse',
          },
        });

        if (delay > 0) {
          tl.set({}, {}, delay);
        }

        tl.fromTo(
          words,
          { opacity: 0.1 },
          {
            opacity: 1,
            duration: wordDuration,
            ease: 'power1.out',
            stagger: { each: staggerEach },
          }
        );
      });
    }, container);

    scheduleScrollTriggerRefresh();

    const onRefreshNeeded = () => scheduleScrollTriggerRefresh();
    window.addEventListener(LENIS_READY_EVENT, onRefreshNeeded);
    window.addEventListener('load', onRefreshNeeded);
    window.addEventListener('resize', onRefreshNeeded);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => scheduleScrollTriggerRefresh());
      const layoutRoot = container.parentElement || document.body;
      resizeObserver.observe(layoutRoot);
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(onRefreshNeeded).catch(() => {});
    }

    return () => {
      window.removeEventListener(LENIS_READY_EVENT, onRefreshNeeded);
      window.removeEventListener('load', onRefreshNeeded);
      window.removeEventListener('resize', onRefreshNeeded);
      resizeObserver?.disconnect();
      ctx.revert();
    };
  }, [
    shouldAnimate,
    selectorKey,
    start,
    end,
    marker,
    pinned,
    staggerEach,
    wordDuration,
    once,
    delay,
  ]);

  if (!shouldAnimate) {
    return children;
  }

  return (
    <div
      ref={containerRef}
      className={classNames(styles.cmsParagraphReveal, className)}
      data-block-id={blockId}
    >
      {children}
    </div>
  );
}
